import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Users, FileText, Receipt, TrendingUp, AlertTriangle, Clock,
  CheckCircle, Send, Calendar, Filter, RefreshCw, ChevronRight,
  DollarSign, AlertCircle
} from 'lucide-react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

export const DashboardPage = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '' });

  const canSeeFinancials = hasRole(['ADMIN', 'MANAGER', 'SECRETARY']);
  const canSeePersonnel = hasRole(['ADMIN']);
  const isEmployee = user?.role === 'EMPLOYEE';

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, activityRes, pendingRes] = await Promise.all([
        api.getDashboardStats(filters),
        api.getRecentActivity(8),
        api.getPendingItems()
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
      setPendingItems(pendingRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => { loadDashboard(); setShowFilters(false); };
  const clearFilters = () => { setFilters({ date_from: '', date_to: '' }); setTimeout(loadDashboard, 100); };

  const getActivityIcon = (type) => {
    if (type === 'INVOICE') return <Receipt className="h-4 w-4 text-blue-500" />;
    if (type === 'WORK_ORDER') return <FileText className="h-4 w-4 text-amber-500" />;
    if (type === 'CUSTOMER') return <Users className="h-4 w-4 text-green-500" />;
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  const formatActivityMessage = (act) => {
    const actions = { CREATED: 'creato', UPDATED: 'aggiornato', SUBMITTED: 'inviato', APPROVED: 'approvato', REJECTED: 'rifiutato', SENT: 'inviato', ISSUED: 'emesso', PAYMENT_REGISTERED: 'pagamento registrato', CANCELLED: 'annullato', REOPENED: 'riaperto' };
    const types = { INVOICE: 'Fattura', WORK_ORDER: 'Bollettino', CUSTOMER: 'Cliente', PAYMENT: 'Pagamento' };
    return `${types[act.entity_type] || act.entity_type} ${actions[act.action] || act.action}`;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><div className="h-20 bg-muted animate-pulse rounded" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Benvenuto, {user?.first_name} {user?.last_name} <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">{user?.role}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="dashboard-filter-btn">
            <Filter className="h-4 w-4 mr-1" /> Filtri
          </Button>
          <Button variant="outline" size="sm" onClick={loadDashboard} data-testid="dashboard-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card data-testid="dashboard-filters-panel">
          <CardContent className="p-4 flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-xs">Data da</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from: e.target.value})} className="w-40" />
            </div>
            <div>
              <Label className="text-xs">Data a</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to: e.target.value})} className="w-40" />
            </div>
            <Button size="sm" onClick={applyFilters}>Applica</Button>
            <Button size="sm" variant="ghost" onClick={clearFilters}>Reset</Button>
          </CardContent>
        </Card>
      )}

      {/* Pending Items Alert */}
      {pendingItems.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="dashboard-pending-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Elementi che richiedono attenzione</span>
            </div>
            {pendingItems.slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:text-amber-700 transition-colors"
                onClick={() => navigate(item.type === 'invoice' ? `/app/invoices/${item.id}` : `/app/work-orders/${item.id}`)}>
                <ChevronRight className="h-3 w-3 text-amber-500" />
                <span>{item.number ? `${item.number}: ` : ''}{item.message}</span>
              </div>
            ))}
            {pendingItems.length > 3 && <p className="text-xs text-amber-600 mt-1">...e altri {pendingItems.length - 3} elementi</p>}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats-grid">
        {/* Bollettini Stats */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/work-orders')} data-testid="stat-work-orders">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <FileText className="h-8 w-8 text-amber-500" />
              <span className="text-3xl font-bold">{stats?.work_orders?.total || 0}</span>
            </div>
            <h3 className="font-semibold text-sm mb-2">Bollettini</h3>
            <div className="space-y-1 text-xs">
              {stats?.work_orders?.in_review > 0 && <div className="flex justify-between"><span className="text-amber-600">In revisione</span><span className="font-medium">{stats.work_orders.in_review}</span></div>}
              {stats?.work_orders?.needs_correction > 0 && <div className="flex justify-between"><span className="text-red-600">Da correggere</span><span className="font-medium">{stats.work_orders.needs_correction}</span></div>}
              {stats?.work_orders?.approved > 0 && <div className="flex justify-between"><span className="text-green-600">Approvati</span><span className="font-medium">{stats.work_orders.approved}</span></div>}
              {stats?.work_orders?.draft > 0 && <div className="flex justify-between"><span className="text-gray-500">Bozze</span><span className="font-medium">{stats.work_orders.draft}</span></div>}
            </div>
          </CardContent>
        </Card>

        {/* Fatture Stats */}
        {canSeeFinancials && (
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/invoices')} data-testid="stat-invoices">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Receipt className="h-8 w-8 text-blue-500" />
                <span className="text-3xl font-bold">{stats?.invoices?.total || 0}</span>
              </div>
              <h3 className="font-semibold text-sm mb-2">Fatture</h3>
              <div className="space-y-1 text-xs">
                {stats?.invoices?.overdue > 0 && <div className="flex justify-between"><span className="text-red-600">Scadute</span><span className="font-medium">{stats.invoices.overdue}</span></div>}
                {stats?.invoices?.pending > 0 && <div className="flex justify-between"><span className="text-amber-600">In sospeso</span><span className="font-medium">{stats.invoices.pending}</span></div>}
                {stats?.invoices?.paid > 0 && <div className="flex justify-between"><span className="text-green-600">Pagate</span><span className="font-medium">{stats.invoices.paid}</span></div>}
                {stats?.invoices?.draft > 0 && <div className="flex justify-between"><span className="text-gray-500">Bozze</span><span className="font-medium">{stats.invoices.draft}</span></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Revenue Stats */}
        {canSeeFinancials && (
          <Card data-testid="stat-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <TrendingUp className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-sm mb-3">Incassi</h3>
              <div className="space-y-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Totale incassato</span>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(stats?.revenue?.total_paid || 0)}</p>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-600">In sospeso</span>
                  <span className="font-medium">{formatCurrency(stats?.revenue?.pending || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-600">Scaduto</span>
                  <span className="font-medium">{formatCurrency(stats?.revenue?.overdue || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customers Stats */}
        {canSeeFinancials && (
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/customers')} data-testid="stat-customers">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Users className="h-8 w-8 text-violet-500" />
                <span className="text-3xl font-bold">{stats?.customers?.total || 0}</span>
              </div>
              <h3 className="font-semibold text-sm mb-2">Clienti</h3>
              <div className="space-y-1 text-xs">
                {stats?.customers?.with_problems > 0 && <div className="flex justify-between"><span className="text-red-600">Con problemi</span><span className="font-medium">{stats.customers.with_problems}</span></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personnel Stats - ADMIN only */}
        {canSeePersonnel && (
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/app/personnel')} data-testid="stat-personnel">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Calendar className="h-8 w-8 text-teal-500" />
                <span className="text-3xl font-bold">{stats?.personnel?.total || 0}</span>
              </div>
              <h3 className="font-semibold text-sm mb-2">Personale</h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-green-600">Attivi</span><span className="font-medium">{stats?.personnel?.active || 0}</span></div>
                {stats?.personnel?.absent_today > 0 && <div className="flex justify-between"><span className="text-amber-600">Assenti oggi</span><span className="font-medium">{stats.personnel.absent_today}</span></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee: My Stats */}
        {isEmployee && (
          <Card data-testid="stat-my-work">
            <CardContent className="p-5">
              <h3 className="font-semibold text-sm mb-3">I Miei Bollettini</h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Totale</span><span className="font-bold">{stats?.work_orders?.total || 0}</span></div>
                <div className="flex justify-between"><span>Approvati</span><span className="font-bold text-green-600">{stats?.work_orders?.approved || 0}</span></div>
                <div className="flex justify-between"><span>Fatturati</span><span className="font-bold text-blue-600">{stats?.work_orders?.invoiced || 0}</span></div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Section: Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-bottom-section">
        {/* Recent Activity */}
        <Card className="lg:col-span-2" data-testid="dashboard-recent-activity">
          <CardHeader className="pb-3"><CardTitle className="text-base">Attivita Recenti</CardTitle></CardHeader>
          <CardContent>
            {activity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nessuna attivita recente</p>
            ) : (
              <div className="space-y-2">
                {activity.map((act, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-2 border-b border-muted last:border-0">
                    {getActivityIcon(act.entity_type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{formatActivityMessage(act)}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {act.user_email} &bull; {new Date(act.timestamp).toLocaleString('it-CH')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-testid="dashboard-quick-actions">
          <CardHeader className="pb-3"><CardTitle className="text-base">Azioni Rapide</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/app/work-orders/new')} data-testid="quick-action-new-wo">
              <FileText className="h-4 w-4 mr-2" /> Nuovo Bollettino
            </Button>
            {canSeeFinancials && (
              <>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/app/customers/new')} data-testid="quick-action-new-customer">
                  <Users className="h-4 w-4 mr-2" /> Nuovo Cliente
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/app/invoices')} data-testid="quick-action-invoices">
                  <Receipt className="h-4 w-4 mr-2" /> Gestisci Fatture
                </Button>
              </>
            )}
            {canSeePersonnel && (
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/app/personnel')} data-testid="quick-action-personnel">
                <Calendar className="h-4 w-4 mr-2" /> Calendario Assenze
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
